/* Copyright (c) 2020-2021 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useForm } from "react-hook-form";
import { useShowError } from "~/components/Error/useShowError";
import { SidebarDropdown } from "~/components/SidebarForm/inputs/SidebarDropdown";
import { SidebarSwitch } from "~/components/SidebarForm/inputs/SidebarSwitch";
import { SidebarTextField } from "~/components/SidebarForm/inputs/SidebarTextField";
import { SidebarForm } from "~/components/SidebarForm/SidebarForm";
import { SidebarFormButtons } from "~/components/SidebarForm/SidebarFormButtons";
import { RequestTableItem } from "~/models";
import { itHasNotOnlyWhitespace } from "~/tools";
import { ServiceViewCtx } from "~/views/services/details/ServiceViewCtx";

type Props = {
  open: boolean;
  setOpen: (value: boolean) => void;
  editedRequest?: RequestTableItem;
  onSubmit: (values: any) => Promise<void>;
};

export default function ({ open, setOpen, editedRequest, onSubmit }: Props) {
  const { service } = React.useContext(ServiceViewCtx);

  const [loading, setLoading] = React.useState(false);

  const methods = useForm();
  const watcher = methods.watch();

  const categoryOptions = service.requestCategories.map((cat) => ({
    value: cat.id,
    label: cat.name,
  }));

  const { showError } = useShowError();

  const submit = async (values: any) => {
    setLoading(true);
    try {
      await onSubmit(values);
      setLoading(false);
      setOpen(false);
    } catch (e) {
      showError(e.response);
      setLoading(false);
    }
  };

  return (
    <SidebarForm
      title={editedRequest ? "Edit request" : "Add new request"}
      open={open}
      setOpen={setOpen}
      formMethods={methods}
      submit={submit}
    >
      <SidebarDropdown
        name="requestCategoryId"
        label="Request Category"
        options={categoryOptions}
        defaultValue={editedRequest?.categoryId}
        required
      />

      <SidebarTextField
        name="name"
        label="Name"
        defaultValue={editedRequest?.name}
        required
        registerOptions={{ validate: itHasNotOnlyWhitespace }}
      />

      <SidebarSwitch
        name="includedInBaseFee"
        label="Included in base fee"
        defaultValue={editedRequest?.includedInBaseFee}
      />
      <SidebarSwitch
        name="extraChargeable"
        label="Extra chargeable"
        defaultValue={editedRequest?.extraChargeable}
      />
      <SidebarSwitch
        name="validInIaaS"
        label="Valid in IaaS"
        defaultValue={editedRequest?.validInIaaS}
      />
      <SidebarSwitch
        name="validInPaaS"
        label="Valid in PaaS"
        defaultValue={editedRequest?.validInPaaS}
      />

      <SidebarFormButtons
        submitText={editedRequest ? "Save" : "Add"}
        submitEnabled={!!(watcher.requestCategoryId && watcher.name)}
        submitLoading={loading}
        onCancel={() => setOpen(false)}
      />
    </SidebarForm>
  );
}
